import React from "react";
import { Box, Container, VStack, HStack, Grid, Heading, Text } from "@chakra-ui/react";
import Navbar from "../components/common/Navbar";
import Footer from "../components/common/Footer";
import Hero from "../components/common/Hero";
import Card from "../components/common/Card";
import CTAButton from "../components/common/CTAButton";
import { FaGraduationCap, FaUsers, FaLightbulb, FaChartLine, FaCertificate, FaHeadset, FaCheckCircle, FaRocket } from "react-icons/fa";

export default function ForFaculty() {
  const benefits = [
    {
      icon: FaLightbulb,
      title: "Continuous Upskilling",
      description: "Stay relevant in the fast-changing tech industry. We provide free training and resources.",
    },
    {
      icon: FaUsers,
      title: "Co-Teaching Model",
      description: "Collaborate with industry experts. Don't compete. Teach together, achieve more.",
    },
    {
      icon: FaChartLine,
      title: "Student Success Boost",
      description: "See your students land better jobs. Improve placement rates. Build college reputation.",
    },
    {
      icon: FaCertificate,
      title: "Industry Alignment",
      description: "Your curriculum becomes market-relevant. Get feedback directly from hiring partners.",
    },
    {
      icon: FaHeadset,
      title: "Support & Resources",
      description: "Access to curriculum materials, teaching guides, assessment tools, and ongoing support.",
    },
    {
      icon: FaGraduationCap,
      title: "Teaching Methods",
      description: "Learn modern pedagogies. Project-based learning. Experiential education. Proven outcomes.",
    },
  ];

  const facultySupport = [
    {
      title: "Curriculum Materials",
      description: "Ready-to-teach modules, lecture slides, coding exercises, and assessments.",
    },
    {
      title: "Industry Mentoring",
      description: "Our mentors co-teach with you. Bring real-world context to classrooms.",
    },
    {
      title: "Assessment Tools",
      description: "Pre-made assessments, rubrics, and evaluation frameworks. Save time on grading.",
    },
    {
      title: "Student Support",
      description: "1-on-1 mentoring, career guidance, and interview prep for students.",
    },
    {
      title: "Professional Development",
      description: "Free certifications, webinars, and workshops to upskill in latest technologies.",
    },
    {
      title: "Analytics Dashboard",
      description: "Track student progress, identify gaps, and improve outcomes real-time.",
    },
  ];

  const coTeachingModel = [
    {
      step: "1. Integration",
      description: "Our instructor attends your classes. Observes your teaching style. Understands your students.",
    },
    {
      step: "2. Collaboration",
      description: "You lead the fundamentals. Our instructor brings industry context and real projects.",
    },
    {
      step: "3. Mentoring",
      description: "Your students get 1-on-1 guidance. You focus on large-group instruction.",
    },
    {
      step: "4. Outcomes",
      description: "Better placements. Happier students. Your reputation grows. Everyone wins.",
    },
  ];

  const whyFacultyMatters = [
    "You're closest to the students - your influence is huge",
    "Your credentials matter - students trust your guidance",
    "You understand the college system - you can bridge the gap",
    "You're invested in long-term outcomes - not just quick placements",
    "You care about actual learning - not just numbers",
  ];

  return (
    <Box>
      <Navbar />

      {/* Hero Section */}
      <Hero
        title="Collaborate, Don't Compete"
        subtitle="Let's work together to transform your students' futures. You handle the fundamentals. We bring industry reality."
        badge="Faculty Partnership"
        withGradient
      >
        <HStack spacing={4} justify="center" flexWrap="wrap">
          <CTAButton href="mailto:faculty@ojasamirai.com" size="lg">
            Join as Faculty Partner
          </CTAButton>
          <CTAButton to="/contact" variant="outline" size="lg">
            Schedule a Call
          </CTAButton>
        </HStack>
      </Hero>

      {/* Why Faculty Matters */}
      <Box bg="#F8FAFC" py={{ base: 12, md: 20 }}>
        <Container maxW="container.xl">
          <VStack spacing={12}>
            <VStack spacing={4} textAlign="center">
              <Heading as="h2" size="2xl" color="#1E293B">
                Why Your Role Is Crucial
              </Heading>
              <Text fontSize="lg" color="#475569" maxW="2xl">
                Faculty are the bridge between theory and practice. You're closest to students. Your influence matters most.
              </Text>
            </VStack>

            <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={8} w="full">
              {whyFacultyMatters.map((item, idx) => (
                <HStack key={idx} spacing={4} p={6} bg="white" borderRadius="lg" border="1px solid #E2E8F0">
                  <Box color="#10B981" fontSize="2xl">
                    <FaCheckCircle />
                  </Box>
                  <Text color="#1E293B" fontWeight="500">
                    {item}
                  </Text>
                </HStack>
              ))}
            </Grid>
          </VStack>
        </Container>
      </Box>

      {/* The Challenge */}
      <Box py={{ base: 12, md: 20 }}>
        <Container maxW="container.xl">
          <VStack spacing={12}>
            <VStack spacing={4} textAlign="center">
              <Heading as="h2" size="2xl" color="#1E293B">
                The Challenge You Face
              </Heading>
              <Text fontSize="lg" color="#475569" maxW="2xl">
                We understand the pressure. Let's solve it together.
              </Text>
            </VStack>

            <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={8} w="full">
              <Box p={8} bg="#FEF3C7" borderRadius="lg">
                <Heading size="sm" color="#D97706" mb={4}>
                  Current Reality
                </Heading>
                <VStack align="start" spacing={3}>
                  <HStack spacing={3}>
                    <Box color="#D97706">✗</Box>
                    <Text color="#78350F">Limited time to upskill</Text>
                  </HStack>
                  <HStack spacing={3}>
                    <Box color="#D97706">✗</Box>
                    <Text color="#78350F">Curriculum feels outdated</Text>
                  </HStack>
                  <HStack spacing={3}>
                    <Box color="#D97706">✗</Box>
                    <Text color="#78350F">Industry moves faster than academics</Text>
                  </HStack>
                  <HStack spacing={3}>
                    <Box color="#D97706">✗</Box>
                    <Text color="#78350F">Students still unemployed after graduation</Text>
                  </HStack>
                  <HStack spacing={3}>
                    <Box color="#D97706">✗</Box>
                    <Text color="#78350F">Competing coaching centers pull top students</Text>
                  </HStack>
                </VStack>
              </Box>

              <Box p={8} bg="#ECFDF5" borderRadius="lg">
                <Heading size="sm" color="#10B981" mb={4}>
                  With Our Partnership
                </Heading>
                <VStack align="start" spacing={3}>
                  <HStack spacing={3}>
                    <Box color="#10B981" fontSize="lg">
                      ✓
                    </Box>
                    <Text color="#065F46">Free upskilling and certifications</Text>
                  </HStack>
                  <HStack spacing={3}>
                    <Box color="#10B981" fontSize="lg">
                      ✓
                    </Box>
                    <Text color="#065F46">Industry-aligned curriculum ready to teach</Text>
                  </HStack>
                  <HStack spacing={3}>
                    <Box color="#10B981" fontSize="lg">
                      ✓
                    </Box>
                    <Text color="#065F46">Stay current without extra effort</Text>
                  </HStack>
                  <HStack spacing={3}>
                    <Box color="#10B981" fontSize="lg">
                      ✓
                    </Box>
                    <Text color="#065F46">Your students get better jobs faster</Text>
                  </HStack>
                  <HStack spacing={3}>
                    <Box color="#10B981" fontSize="lg">
                      ✓
                    </Box>
                    <Text color="#065F46">College reputation soars</Text>
                  </HStack>
                </VStack>
              </Box>
            </Grid>
          </VStack>
        </Container>
      </Box>

      {/* Co-Teaching Model */}
      <Box bg="#F8FAFC" py={{ base: 12, md: 20 }}>
        <Container maxW="container.xl">
          <VStack spacing={12}>
            <VStack spacing={4} textAlign="center">
              <Heading as="h2" size="2xl" color="#1E293B">
                How Co-Teaching Works
              </Heading>
              <Text fontSize="lg" color="#475569" maxW="2xl">
                You're in control. We're here to support and enhance what you already do.
              </Text>
            </VStack>

            <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" }} gap={6} w="full">
              {coTeachingModel.map((item, idx) => (
                <Box key={idx} p={6} bg="white" border="2px solid #10B981" borderRadius="lg">
                  <Box fontSize="2xl" fontWeight="bold" color="#10B981" mb={4}>
                    {item.step}
                  </Box>
                  <Text color="#475569" fontSize="sm">
                    {item.description}
                  </Text>
                </Box>
              ))}
            </Grid>
          </VStack>
        </Container>
      </Box>

      {/* Benefits Grid */}
      <Box py={{ base: 12, md: 20 }}>
        <Container maxW="container.xl">
          <VStack spacing={12}>
            <VStack spacing={4} textAlign="center">
              <Heading as="h2" size="2xl" color="#1E293B">
                What You Get
              </Heading>
              <Text fontSize="lg" color="#475569" maxW="2xl">
                Everything to make teaching industry-relevant skills effortless.
              </Text>
            </VStack>

            <Grid
              templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }}
              gap={8}
              w="full"
            >
              {benefits.map((item, idx) => (
                <Card
                  key={idx}
                  icon={item.icon}
                  title={item.title}
                  description={item.description}
                />
              ))}
            </Grid>
          </VStack>
        </Container>
      </Box>

      {/* Faculty Support */}
      <Box bg="#F8FAFC" py={{ base: 12, md: 20 }}>
        <Container maxW="container.xl">
          <VStack spacing={12}>
            <VStack spacing={4} textAlign="center">
              <Heading as="h2" size="2xl" color="#1E293B">
                Faculty Support & Resources
              </Heading>
              <Text fontSize="lg" color="#475569" maxW="2xl">
                We provide comprehensive support so you can focus on teaching.
              </Text>
            </VStack>

            <Grid
              templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }}
              gap={8}
              w="full"
            >
              {facultySupport.map((item, idx) => (
                <Box key={idx} p={6} bg="white" borderRadius="lg" border="1px solid #E2E8F0">
                  <Heading size="sm" color="#1E293B" mb={3}>
                    {item.title}
                  </Heading>
                  <Text color="#475569" fontSize="sm">
                    {item.description}
                  </Text>
                </Box>
              ))}
            </Grid>
          </VStack>
        </Container>
      </Box>

      {/* Professional Development */}
      <Box py={{ base: 12, md: 20 }}>
        <Container maxW="container.xl">
          <VStack spacing={12}>
            <VStack spacing={4} textAlign="center">
              <Heading as="h2" size="2xl" color="#1E293B">
                Professional Development for Faculty
              </Heading>
              <Text fontSize="lg" color="#475569" maxW="2xl">
                Stay at the cutting edge. Free training. Industry certifications. Webinars with practitioners.
              </Text>
            </VStack>

            <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={8} w="full">
              <Box p={8} bg="white" borderRadius="lg" border="1px solid #E2E8F0">
                <HStack mb={4}>
                  <Box color="#10B981" fontSize="2xl">
                    <FaRocket />
                  </Box>
                  <Heading size="md" color="#1E293B">
                    Technical Training
                  </Heading>
                </HStack>
                <VStack align="start" spacing={3}>
                  <Text color="#475569">• Modern programming languages</Text>
                  <Text color="#475569">• Cloud platforms and DevOps</Text>
                  <Text color="#475569">• AI/ML fundamentals</Text>
                  <Text color="#475569">• Mobile and Web development</Text>
                  <Text color="#475569">• Data science and analytics</Text>
                </VStack>
              </Box>

              <Box p={8} bg="white" borderRadius="lg" border="1px solid #E2E8F0">
                <HStack mb={4}>
                  <Box color="#10B981" fontSize="2xl">
                    <FaCertificate />
                  </Box>
                  <Heading size="md" color="#1E293B">
                    Industry Certifications
                  </Heading>
                </HStack>
                <VStack align="start" spacing={3}>
                  <Text color="#475569">• AWS/GCP/Azure certifications</Text>
                  <Text color="#475569">• Kubernetes and containerization</Text>
                  <Text color="#475569">• TensorFlow and PyTorch</Text>
                  <Text color="#475569">• React and modern frameworks</Text>
                  <Text color="#475569">• + 20 more specialized tracks</Text>
                </VStack>
              </Box>
            </Grid>
          </VStack>
        </Container>
      </Box>

      {/* FAQs */}
      <Box bg="#F8FAFC" py={{ base: 12, md: 20 }}>
        <Container maxW="container.xl">
          <VStack spacing={12}>
            <VStack spacing={4} textAlign="center">
              <Heading as="h2" size="2xl" color="#1E293B">
                Questions About Faculty Partnership?
              </Heading>
              <Text fontSize="lg" color="#475569" maxW="2xl">
                We have answers. And we're here to address any concerns.
              </Text>
            </VStack>

            <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={8} w="full">
              <Box p={6} bg="white" borderRadius="lg">
                <Heading size="sm" color="#1E293B" mb={3}>
                  Will this overload my teaching?
                </Heading>
                <Text color="#475569">
                  No. We provide all materials, assessments, and support. You teach what we prepare. Our instructor co-teaches, taking some load off.
                </Text>
              </Box>

              <Box p={6} bg="white" borderRadius="lg">
                <Heading size="sm" color="#1E293B" mb={3}>
                  Do I have to change my teaching style?
                </Heading>
                <Text color="#475569">
                  Not at all. We adapt to your style. Our focus is adding real-world context and projects to what you already teach.
                </Text>
              </Box>

              <Box p={6} bg="white" borderRadius="lg">
                <Heading size="sm" color="#1E293B" mb={3}>
                  How much time does this take?
                </Heading>
                <Text color="#475569">
                  Same time as teaching normally. You already teach the subject. We just enhance it with industry relevance and projects.
                </Text>
              </Box>

              <Box p={6} bg="white" borderRadius="lg">
                <Heading size="sm" color="#1E293B" mb={3}>
                  Is there a commitment period?
                </Heading>
                <Text color="#475569">
                  Flexible. Start with one semester. If you love it, expand. No long-term contracts that lock you in.
                </Text>
              </Box>

              <Box p={6} bg="white" borderRadius="lg">
                <Heading size="sm" color="#1E293B" mb={3}>
                  How do students benefit?
                </Heading>
                <Text color="#475569">
                  Better jobs. Your teaching + real projects + industry mentoring = 85%+ placement within 3 months.
                </Text>
              </Box>

              <Box p={6} bg="white" borderRadius="lg">
                <Heading size="sm" color="#1E293B" mb={3}>
                  What if I want to opt out?
                </Heading>
                <Text color="#475569">
                  You can leave anytime. No penalties. But we're confident once you see results, you'll want to continue.
                </Text>
              </Box>
            </Grid>
          </VStack>
        </Container>
      </Box>

      {/* Final CTA */}
      <Box
        bgGradient="linear(to-br, #0F172A, #1E293B, #10B981)"
        py={{ base: 12, md: 20 }}
        color="white"
      >
        <Container maxW="container.xl">
          <VStack spacing={8} textAlign="center">
            <Heading as="h2" size="2xl" color="white">
              Let's Transform Education Together
            </Heading>
            <Text fontSize="lg" maxW="2xl" color="#FFFFFF">
              Join faculty across India who are bridging the gap between theory and practice. Your students deserve better outcomes.
            </Text>

            <HStack spacing={4} justify="center" flexWrap="wrap">
              <CTAButton href="mailto:faculty@ojasamirai.com" size="lg">
                Express Your Interest
              </CTAButton>
              <CTAButton to="/contact" variant="outline" size="lg">
                Schedule a Discussion
              </CTAButton>
            </HStack>
          </VStack>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
}
